import type { RefObject } from "react";
import { Fragment, useEffect, useRef, useState } from "react";

import * as styles from "./atom.description.style";
import {
  useMatchBreakpoint,
  breakpoints,
  useToggleState,
  SizeTransition,
  Chevron,
} from "@pm2/compat";
import { AudioModel } from "@courses-pm2/api";

type Props = {
  title: string;
  isScrambled: boolean;
  caption: AudioModel["caption"];
};

const PUNCTUATION_REGEX = /[.,!?]\s*$/;

export function Description(props: Props) {
  const descriptionRef = useRef<HTMLElement>(null);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);

  const [isDescriptionExpanded, expansionActions] = useToggleState();
  const canExpandDescription = useCanExpand(descriptionRef, {
    enabled: !props.isScrambled,
  });

  const captionModel = props.caption;

  const displayDescription =
    captionModel?.label ||
    captionModel?.authorOrArtist ||
    captionModel?.country ||
    captionModel?.duration ||
    captionModel?.year ||
    captionModel?.creditLine;

  if (!displayDescription) {
    return null;
  }

  const description = [
    captionModel.label && (
      <span
        key="label"
        dangerouslySetInnerHTML={{ __html: captionModel.label }}
        data-comma={!PUNCTUATION_REGEX.test(captionModel.label)}
      />
    ),
    captionModel.authorOrArtist && (
      <span
        key="authorOrArtist"
        dangerouslySetInnerHTML={{ __html: captionModel.authorOrArtist }}
        data-comma={!PUNCTUATION_REGEX.test(captionModel.authorOrArtist)}
      />
    ),
    captionModel.country && (
      <span
        key="country"
        dangerouslySetInnerHTML={{ __html: captionModel.country }}
        data-comma={!PUNCTUATION_REGEX.test(captionModel.country)}
      />
    ),
    captionModel.duration && (
      <span
        key="duration"
        dangerouslySetInnerHTML={{ __html: captionModel.duration }}
        data-comma={!PUNCTUATION_REGEX.test(captionModel.duration)}
      />
    ),
    captionModel.year && (
      <span
        key="year"
        dangerouslySetInnerHTML={{ __html: captionModel.year }}
        data-comma={!PUNCTUATION_REGEX.test(captionModel.year)}
      />
    ),
    captionModel.creditLine && (
      <span
        key="creditLine"
        dangerouslySetInnerHTML={{
          __html: "©&nbsp;" + captionModel.creditLine,
        }}
        data-comma={!PUNCTUATION_REGEX.test(captionModel.creditLine)}
      />
    ),
  ].filter((it): it is React.JSX.Element => !!it);

  const hasTitle = !!props.title;

  return (
    <div className={styles.wrapper}>
      <SizeTransition
        timeout={styles.descriptionTransitionDurationMs}
        axis="vertical"
        in={isDescriptionExpanded}
        classNames={styles.description}
        getMinSizeInPx={() => ({
          height: isMobile ? (hasTitle ? 0 : 19) : hasTitle ? 44 : 66,
        })}
        nodeRef={descriptionRef}
      >
        {(ref) => (
          <p
            ref={ref}
            className={styles.description}
            data-expanded={isDescriptionExpanded}
            data-scrambled={props.isScrambled}
          >
            {description.map((it, index) => (
              <Fragment key={index}>
                {it}
                <span>
                  {index < description.length - 1 &&
                    (it.props["data-comma"] === true ? ", " : " ")}
                </span>
              </Fragment>
            ))}
          </p>
        )}
      </SizeTransition>

      <div className={styles.descriptionTogglerWrapper}>
        {canExpandDescription && (
          <button
            onClick={expansionActions.toggle}
            className={styles.descriptionToggler}
          >
            <span>
              {isMobile
                ? isDescriptionExpanded
                  ? "Skjul beskrivelse"
                  : "Se beskrivelse"
                : isDescriptionExpanded
                ? "Se mindre"
                : "Se mere"}
            </span>{" "}
            <Chevron
              vertical
              size={8}
              lineWidth={1.5}
              inversed={isDescriptionExpanded}
            />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * the introduction should become expandable/collapsible in case there exists
 * more than three lines of text or in case any media is included in the
 * introduction.
 */
function useCanExpand(
  ref: RefObject<HTMLElement | null>,
  args: { enabled: boolean },
) {
  const [canExpand, setCanExpand] = useState(args.enabled);

  useEffect(() => {
    if (!args.enabled) {
      setCanExpand(false);
      return;
    }

    const element = ref.current;

    if (!element) {
      return;
    }

    setCanExpand(element.scrollHeight > element.clientHeight);
  }, [args.enabled, ref]);

  return canExpand;
}
